<script setup lang="ts">
import { ref } from 'vue'

const comicPanels = ref([
  {
    id: 1,
    title: 'Onze start',
    year: '2025',
    description: 'Gestart met een gek idee en een passie voor speelse kinderkampen.',
    bgColor: '#F9D4D0',
    rotation: -2,
    illustration: 'bx-rocket',
    speechBubble: 'De Reis Van \'t SpeelFabrik',
  },
  {
    id: 2,
    title: 'Onze Missie',
    year: 'Nu',
    description: 'Wij bouwen mee aan leuke momenten waar beweging & creativiteit centraal staat.',
    bgColor: '#B4E1FF',
    rotation: 2,
    illustration: 'bx-bxs-droplet',
    speechBubble: 'Bouwen, spelen, groeien!',
  },
  {
    id: 3,
    title: 'Onze Impact',
    year: '1000+',
    description: 'Hoopvol om heel wat blije gezichtjes te zien tijdens onze activiteiten',
    bgColor: '#FFE5B4',
    rotation: -1,
    illustration: 'bx-bxs-star',
    speechBubble: 'De leukste kinderactiviteiten',
  },
])
</script>

<template>
  <div class="comic-section">
    <VContainer>
      <div class="comic-header">
        <div class="comic-badge">
          <VIcon icon="bx-bxs-book-bookmark" />
          <span>Ons Verhaal</span>
        </div>
        <h2 class="comic-title">
          kom mee op ontdekking
        </h2>
      </div>

      <div class="comic-strip">
        <div
          v-for="panel in comicPanels"
          :key="panel.id"
          class="comic-panel"
          :style="{
            backgroundColor: panel.bgColor,
            transform: `rotate(${panel.rotation}deg)`,
          }"
        >
          <div class="panel-border">
            <div class="panel-header">
              <div class="panel-number">
                {{ panel.id }}
              </div>
              <div class="panel-year">
                {{ panel.year }}
              </div>
            </div>

            <div class="panel-illustration">
              <VIcon
                :icon="panel.illustration"
                size="80"
                class="illustration-icon"
              />
              <div class="star-burst" />
            </div>

            <h3 class="panel-title">
              {{ panel.title }}
            </h3>

            <p class="panel-description">
              {{ panel.description }}
            </p>

            <div class="speech-bubble">
              <div class="bubble-text">
                {{ panel.speechBubble }}
              </div>
              <div class="bubble-tail" />
            </div>

            <!-- Comic style dots -->
            <div class="comic-dots">
              <div
                v-for="i in 12"
                :key="i"
                class="dot"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="comic-cta">
        <div class="arrow-line">
          <VIcon
            icon="mdi-arrow-right"
            size="32"
          />
          <VIcon
            icon="mdi-arrow-right"
            size="32"
          />
          <VIcon
            icon="mdi-arrow-right"
            size="32"
          />
        </div>
        <VBtn
          size="x-large"
          class="cta-button"
          href="#"
        >
          <span class="button-text">Leer ons beter kennen</span>
          <div class="button-sparkles">
            <VIcon
              icon="mdi-star"
              size="16"
            />
            <VIcon
              icon="mdi-star"
              size="12"
            />
            <VIcon
              icon="mdi-star"
              size="14"
            />
          </div>
        </VBtn>
      </div>
    </VContainer>

    <!-- Background elements -->
    <div class="comic-bg">
      <div
        v-for="i in 20"
        :key="i"
        class="bg-star"
        :style="{
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 3}s`,
        }"
      >
        ✦
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.comic-section {
  position: relative;
  overflow: hidden;
  background: rgb(var(--v-theme-background));
  padding-block: 5rem;
}

.comic-header {
  margin-block-end: 3rem;
  text-align: center;
}

.comic-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 8px;
  background: rgba(242, 112, 99, 10%);
  color: #f27063;
  font-family: "Peachy Kee JF", "Public Sans", sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  gap: 0.5rem;
  letter-spacing: 0.5px;
  margin-block-end: 1.5rem;
  padding-block: 0.5rem;
  padding-inline: 1.5rem;
  text-transform: uppercase;

  .v-icon {
    color: currentcolor;
  }
}

.comic-title {
  margin: 0;
  color: rgb(var(--v-theme-on-surface));
  font-family: "Peachy Kee JF", "Public Sans", sans-serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  line-height: 1.2;
  margin-block-end: 1rem;
}

.comic-strip {
  display: grid;
  gap: 3rem;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  margin-block-end: 4rem;
  margin-inline: auto;
  max-inline-size: 1400px;
  padding-block: 2rem;
}

.comic-panel {
  overflow: hidden;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 2px 12px rgba(0 0 0 / 8%);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 24px rgba(0 0 0 / 12%);
    transform: translateY(-4px) !important;
  }
}

.panel-border {
  position: relative;
  overflow: hidden;
  padding: 2rem;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  margin-block-end: 1.5rem;
}

.panel-number {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(44 62 80 / 10%);
  block-size: 36px;
  color: #2c3e50;
  font-family: "Peachy Kee JF", "Public Sans", sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  inline-size: 36px;
}

.panel-year {
  border-radius: 8px;
  background: rgba(242 112 99 / 10%);
  color: #f27063;
  font-family: "Peachy Kee JF", "Public Sans", sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  padding-block: 0.25rem;
  padding-inline: 0.75rem;
}

.panel-illustration {
  position: relative;
  margin-block-end: 1.5rem;
  text-align: center;
}

.illustration-icon {
  position: relative;
  z-index: 2;
  color: #333;
}

.star-burst {
  position: absolute;
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
  background: rgba(255, 215, 0, 20%);
  block-size: 120px;
  inline-size: 120px;
  inset-block-start: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%);
}

.panel-title {
  color: #2c3e50;
  font-family: "Peachy Kee JF", "Public Sans", sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  margin-block-end: 1rem;
  text-align: center;
}

.panel-description {
  color: #555;
  font-size: 1rem;
  line-height: 1.6;
  margin-block-end: 1.5rem;
  text-align: center;
}

.speech-bubble {
  position: relative;
  border: 1px solid rgba(0 0 0 / 8%);
  border-radius: 12px;
  background: rgba(0 0 0 / 2%);
  margin-block-start: 1.5rem;
  padding-block: 1rem;
  padding-inline: 1.5rem;
}

.bubble-text {
  color: #333;
  font-family: "Peachy Kee JF", "Public Sans", sans-serif;
  font-size: 0.95rem;
  font-style: italic;
  font-weight: 500;
  text-align: center;
}

.bubble-tail {
  display: none;
}

.comic-dots {
  position: absolute;
  display: grid;
  padding: 1rem;
  gap: 1rem;
  grid-template-columns: repeat(4, 1fr);
  inset: 0;
  opacity: 0.1;
  pointer-events: none;
}

.dot {
  border-radius: 50%;
  background: #333;
  block-size: 8px;
  inline-size: 8px;
}

.comic-cta {
  text-align: center;
}

.arrow-line {
  display: flex;
  justify-content: center;
  animation: arrow-bounce 1.5s ease-in-out infinite;
  color: #f27063;
  gap: 0.5rem;
  margin-block-end: 1.5rem;
}

.cta-button {
  position: relative;
  border-radius: 8px;
  background: #f27063;
  box-shadow: 0 4px 12px rgba(242 112 99 / 30%);
  color: #fff;
  font-family: "Peachy Kee JF", "Public Sans", sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding-block: 0.75rem;
  padding-inline: 2rem;
  text-transform: uppercase;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 20px rgba(242 112 99 / 40%);
    transform: translateY(-2px);
  }
}

.button-sparkles {
  position: absolute;
  display: flex;
  animation: sparkle 1.5s ease-in-out infinite;
  color: #ffd700;
  gap: 0.25rem;
  inset-block-start: -10px;
  inset-inline-end: -10px;
}

.comic-bg {
  display: none;
}

.bg-star {
  display: none;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.3;
    transform: translate(-50%, -50%) scale(1);
  }

  50% {
    opacity: 0.5;
    transform: translate(-50%, -50%) scale(1.1);
  }
}

@keyframes arrow-bounce {
  0%,
  100% {
    transform: translateX(0);
  }

  50% {
    transform: translateX(10px);
  }
}

@keyframes sparkle {
  0%,
  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }

  50% {
    opacity: 0.7;
    transform: scale(1.2) rotate(180deg);
  }
}

@keyframes twinkle {
  0%,
  100% {
    opacity: 0.2;
    transform: scale(1);
  }

  50% {
    opacity: 0.5;
    transform: scale(1.2);
  }
}

@media (max-width: 960px) {
  .comic-title {
    font-size: 2.5rem;
  }

  .comic-strip {
    gap: 2rem;
    grid-template-columns: 1fr;
  }

  .comic-panel {
    transform: rotate(0deg) !important;
  }
}

@media (max-width: 600px) {
  .comic-section {
    padding-block: 3rem;
  }

  .comic-title {
    font-size: 2rem;
  }

  .panel-title {
    font-size: 1.5rem;
  }

  .cta-button {
    font-size: 1.25rem;
    padding-block: 1rem;
    padding-inline: 2.5rem;
  }
}
</style>
